import { createContext, useCallback, useContext, useEffect, useState, type ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import { getLocalisation } from '@/api/localisationApi';
import type { Field, Localisation } from '@/api/types';

const LOCALISATION_CACHE_KEY = 'iplan.web.localisation';

interface LocalisationProviderProps {
  children: ReactNode;
}

export const LocalisationContext = createContext<Localisation | undefined>(undefined);

export const useLocalisationContext = (): Localisation => {
  const localisation = useContext(LocalisationContext);
  if (localisation === undefined) {
    throw new Error('useLocalisationContext must be used within a LocalisationProvider');
  }

  const getTokenValue = useCallback(
    (name: string): string => {
      const token = localisation.tokens.find((t) => t.name === name);
      return token ? token.value : '';
    },
    [localisation.tokens],
  );

  const getField = useCallback(
    (name: string): Field => {
      const field = localisation.fields.find((f) => f.name === name) ?? {
        name: '',
        displayName: name,
        type: 'text',
        required: false,
        visible: true,
        readOnly: false,
        sortOrder: 0,
        value: '',
        isFilterable: false,
        isSortable: false,
        isCurrency: false,
        propertyName: '',
        summaryVisible: true,
      };
      return { ...field };
    },
    [localisation.fields],
  );

  return { ...localisation, getTokenValue, getField };
};

function readCachedLocalisation(): Localisation | undefined {
  if (typeof window === 'undefined') return undefined;
  try {
    const raw = window.localStorage.getItem(LOCALISATION_CACHE_KEY);
    return raw ? (JSON.parse(raw) as Localisation) : undefined;
  } catch {
    return undefined;
  }
}

function writeCachedLocalisation(value: Localisation | undefined): void {
  if (typeof window === 'undefined') return;
  try {
    if (value === undefined) {
      window.localStorage.removeItem(LOCALISATION_CACHE_KEY);
    } else {
      window.localStorage.setItem(LOCALISATION_CACHE_KEY, JSON.stringify(value));
    }
  } catch {
    // Storage may be unavailable (private mode, quota exceeded). Ignore.
  }
}

export const LocalisationProvider = ({ children }: LocalisationProviderProps): ReactNode => {
  const [cachedLocalisation, setCachedLocalisation] = useState<Localisation | undefined>(() =>
    readCachedLocalisation(),
  );

  const { data, isLoading, error } = useQuery<Localisation>({
    queryKey: ['localizationData'],
    queryFn: getLocalisation,
    refetchOnWindowFocus: false,
    retry: typeof process !== 'undefined' && process.env?.NODE_ENV === 'test' ? 0 : 10,
    retryDelay: (retryAttempt: number) => Math.min(1000 * 2 ** retryAttempt, 30000),
    staleTime: 12 * 60 * 60 * 1000,
    gcTime: 12 * 60 * 60 * 1000,
    refetchOnMount: 'always',
    refetchInterval: 30 * 60 * 1000,
    refetchIntervalInBackground: true,
    // placeholderData (not initialData) so the cached set renders instantly but
    // the query still always fetches on mount (initialData would mark it fresh).
    placeholderData: cachedLocalisation,
  });

  useEffect(() => {
    if (data) {
      setCachedLocalisation(data);
      writeCachedLocalisation(data);
    }
  }, [data]);

  if (error) {
    // eslint-disable-next-line no-console
    console.error('Error fetching localisation:', error);
    return (
      <>
        <div>Error: {(error as Error).message}</div>
        {children}
      </>
    );
  }

  if (isLoading && !cachedLocalisation) {
    return (
      <div className="flex h-full items-center justify-center p-12">
        <div
          className="h-6 w-6 animate-spin rounded-full border-2 border-iplan-brand border-t-transparent"
          aria-label="Loading Localisation..."
        />
      </div>
    );
  }

  return (
    <LocalisationContext.Provider value={data ?? cachedLocalisation}>
      {children}
    </LocalisationContext.Provider>
  );
};
